import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { FormInput, FormPhone } from '@podus/ui';
import { useSendOtpMutation } from '@/features/api/authApi';
import { useAppDispatch } from '@/hooks/useStore';
import { setPendingVerification } from '@/features/auth/authSlice';
import { isAllowedRedirect, buildRedirectUrl } from '@/lib/redirect';
import { clearAuthCookies, getAccessToken } from '@/lib/cookies';
import { isTokenValid } from '@/lib/validateToken';

type LoginMethod = 'phone' | 'email';

export default function SignIn() {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [searchParams] = useSearchParams();
    const redirectParam = searchParams.get('redirect');

    // Auto-redirect if user is already logged in AND their token is still
    // valid server-side. Without the validation, a revoked / expired token
    // would loop the user between this page and the target app.
    useEffect(() => {
        const existingToken = getAccessToken();
        if (!existingToken || !redirectParam || !isAllowedRedirect(redirectParam)) return;

        let cancelled = false;
        (async () => {
            const valid = await isTokenValid(existingToken);
            if (cancelled) return;
            if (valid) {
                window.location.href = buildRedirectUrl(redirectParam, existingToken);
            } else {
                clearAuthCookies();
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [redirectParam]);

    const [method, setMethod] = useState<LoginMethod>('phone');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [error, setError] = useState('');
    const dialCodeRef = useRef('');

    const [sendOtp, { isLoading }] = useSendOtpMutation();

    const sanitizedPhone = useMemo(() => phone.replace(/\D/g, ''), [phone]);
    const trimmedEmail = useMemo(() => email.trim(), [email]);

    // Track initial dial code to detect actual user input
    useEffect(() => {
        if (sanitizedPhone.length === 0) {
            dialCodeRef.current = '';
        } else if (!dialCodeRef.current || sanitizedPhone.length < dialCodeRef.current.length) {
            dialCodeRef.current = sanitizedPhone;
        }
    }, [sanitizedPhone]);

    const isEmailMethod = method === 'email';
    const hasEmailValue = trimmedEmail.length > 0;
    const hasPhoneValue = dialCodeRef.current
        ? sanitizedPhone.length > dialCodeRef.current.length
        : sanitizedPhone.length > 0;
    const isSubmitDisabled = isEmailMethod ? !hasEmailValue : !hasPhoneValue;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (isSubmitDisabled) return;
        setError('');

        const body = isEmailMethod ? { email: trimmedEmail } : { phoneNumber: sanitizedPhone };

        try {
            await sendOtp(body).unwrap();

            dispatch(
                setPendingVerification({
                    flow: 'login',
                    ...(isEmailMethod ? { email: trimmedEmail } : { phoneNumber: sanitizedPhone }),
                    ...(redirectParam && isAllowedRedirect(redirectParam) ? { redirectUrl: redirectParam } : {}),
                }),
            );

            navigate('/verify-otp');
        } catch (err: unknown) {
            const apiErr = err as {
                data?: { message?: string; response?: { message?: string } };
            };
            setError(
                apiErr?.data?.response?.message ?? apiErr?.data?.message ?? 'Failed to send OTP. Please try again.',
            );
        }
    };

    return (
        <AuthLayout>
            <form onSubmit={handleSubmit} className="flex flex-col">
                <h1 className="text-2xl font-bold leading-tight tracking-[-0.5px] text-foreground sm:text-[32px] sm:leading-10">
                    Sign in to your account
                </h1>

                <div className="mt-8 flex flex-col gap-3 sm:mt-12 sm:gap-4">
                    {/* Phone / Email toggle with sliding pill */}
                    <div className="relative flex items-center gap-1 rounded-full border border-input bg-background p-1 text-sm font-medium">
                        {/* Sliding pill background */}
                        <div
                            className={`toggle-pill absolute top-1 bottom-1 w-[calc(50%-4px)] rounded-full bg-btn ${method === 'email' ? 'toggle-pill-right' : 'toggle-pill-left'}`}
                        />
                        <button
                            type="button"
                            onClick={() => setMethod('phone')}
                            className={`relative z-10 flex-1 cursor-pointer rounded-full px-4 py-2 transition-colors duration-300 ${
                                method === 'phone' ? 'text-btn-foreground' : 'text-foreground hover:bg-muted'
                            }`}
                        >
                            Phone
                        </button>
                        <button
                            type="button"
                            onClick={() => setMethod('email')}
                            className={`relative z-10 flex-1 cursor-pointer rounded-full px-4 py-2 transition-colors duration-300 ${
                                method === 'email' ? 'text-btn-foreground' : 'text-foreground hover:bg-muted'
                            }`}
                        >
                            Email
                        </button>
                    </div>

                    {/* Error message */}
                    {error && (
                        <div className="rounded-lg border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm text-destructive">
                            {error}
                        </div>
                    )}

                    {/* Input fields with enter animation */}
                    <div key={method} className="field-enter">
                        {method === 'email' ? (
                            <FormInput
                                id="login-email"
                                name="email"
                                type="email"
                                title="Email"
                                placeholder="johndoe@gmail.com"
                                autoComplete="email"
                                required
                                variant="white"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />
                        ) : (
                            <FormPhone
                                id="login-phone"
                                name="phone"
                                title="Phone Number *"
                                value={phone}
                                onChange={setPhone}
                                required
                                defaultCountry="ng"
                                // variant="white"
                            />
                        )}
                    </div>
                </div>

                {/* Sign In Button */}
                <button
                    type="submit"
                    disabled={isSubmitDisabled || isLoading}
                    className="mt-6 h-11 w-full cursor-pointer rounded-[10px] bg-btn text-sm font-bold leading-6 text-btn-foreground transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60 sm:mt-7 sm:h-12"
                >
                    {isLoading ? 'Sending OTP...' : 'Continue'}
                </button>

                <p className="mt-4 text-center text-sm leading-6 text-foreground">
                    Don&rsquo;t have an account?{' '}
                    <Link
                        to={redirectParam ? `/sign-up?redirect=${encodeURIComponent(redirectParam)}` : '/sign-up'}
                        className="font-bold text-primary"
                    >
                        Get started here
                    </Link>
                </p>
            </form>
        </AuthLayout>
    );
}
